import React, { useEffect, useState } from "react";

import { ColumnsType } from "antd/es/table";

import { cellWidth } from "../../../core/methods/tableCellWidth";
import Datatable from "../../../core/common/dataTable";
import TimelineModal from "../../../core/common/TimelineModal";

import {
  parseNumber,
  safeLocaleCompare,
} from "../../../core/methods/sorterMethods";

import DynamicSingleSelect, {
  FilterTypes,
  FilterErrors,
  initialFilters,
} from "../reusable/DynamicSingleSelect";

import Loading from "../../../core/common/custom-loading/Loading";
import webservice_url from "../../../core/webservice/Webservice_url";
import useApi from "../../../core/customhook/useApi";
import { PaginationType } from "../../../types/common-type";
import useLocalStorage from "../../../core/customhook/hooks/useLocalStorage";
import dayjs from "dayjs";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";
import { filterByColumnSearch } from "../utils/filterByColumnSearch";

type Props = {
  status_id: string;
};

const StoppedCases: React.FC<Props> = ({ status_id }) => {
  const { setLocalStorage, getLocalStorage } = useLocalStorage();
  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);
  const [filterErrors, setFilterErrors] = useState<FilterErrors>({});
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  const { callApi: searchCall, loading } = useApi<any>(
    webservice_url.fetch_hr_pending_action_data,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;

    setShowTimeline(true);
    setTimelineLoading(true);

    try {
      const res = await getCaseLog.callApi({
        case_id: caseId,
      });

      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  type InviteItem = any;

  // const filteredData = (Array.isArray(allItemList) ? allItemList : []).filter(
  //   (item: InviteItem) => {
  //     return Object.entries(inventoryColumnSearch).every(([key, value]) => {
  //       if (!value) return true;

  //       return String(item[key as keyof InviteItem] ?? "")
  //         .toLowerCase()
  //         .includes(value.toLowerCase());
  //     });
  //   },
  // );
  const filteredData = filterByColumnSearch(
    Array.isArray(allItemList) ? allItemList : [],
    inventoryColumnSearch,
  );

  const fetchData = async (
    filters: FilterTypes = filterData,
    page: number = pagination.current,
    pageSize: number = pagination.pageSize,
  ) => {
    try {
      const payload = {
        status: status_id ?? "",

        page,
        limit: pageSize,

        org_ids: filters?.organization ?? [],
        project_ids: filters?.project ?? [],
        location_ids: filters?.location ?? [],
        program_ids: filters?.program ?? [],
        department_ids: filters?.department ?? [],
      };

      const res = await searchCall(payload);

      if (res?.success) {
        setAllItemList(res?.data?.rows || []);

        setPagination((prev) => ({
          ...prev,
          total: res?.data?.pagination?.totalRecords || 0,
        }));
      } else {
        setAllItemList([]);
      }
    } catch (error: any) {
      console.error("Stopped Cases API Error:", error?.response || error);

      setAllItemList([]);
    }
  };

  useEffect(() => {
    fetchData(filterData, pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };
  const handleFilterChange = (name: keyof FilterTypes, value: string[]) => {
    setFilterData((prev) => {
      const updated: FilterTypes = {
        ...prev,
        [name]: value,
      };

      if (name === "organization") {
        updated.project = [];
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      if (name === "project") {
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      if (name === "location") {
        updated.program = [];
        updated.department = [];
      }

      if (name === "program") {
        updated.department = [];
      }

      return updated;
    });

    if (value.length > 0) {
      setFilterErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSearch = async () => {
    const errors: FilterErrors = {};

    // Organization mandatory
    if (!filterData.organization || filterData.organization.length === 0) {
      errors.organization = "Organization is required";
    }

    // Account / Project mandatory
    if (!filterData.project || filterData.project.length === 0) {
      errors.project = "Account is required";
    }

    setFilterErrors(errors);

    // Stop if validation fails
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Save filters
    setLocalStorage("admin-stopped-cases-filters", filterData);

    // Search should start from page 1
    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    // Already on page 1
    await fetchData(filterData, 1, pagination.pageSize);
  };

  const handleClearAll = async () => {
    const clearedFilters: FilterTypes = {
      organization: [],
      project: [],
      location: [],
      program: [],
      department: [],
    };

    setFilterData(clearedFilters);
    setFilterErrors({});

    localStorage.removeItem("admin-stopped-cases-filters");

    // Reset pagination
    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    // Already page 1
    await fetchData(clearedFilters, 1, pagination.pageSize);
  };
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },

    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "applicant_name",

      onCell: () => cellWidth(200),

      render: (text) => <div className="text-capitalize">{text}</div>,

      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "applicant_name"),
    },

    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),

      dataIndex: "case_id",

      onCell: () => cellWidth(190),

      sorter: (a, b) => parseNumber(a.case_id) - parseNumber(b.case_id),
    },

    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc",
        setInventoryColumnSearch,
      ),

      dataIndex: "case_spoc",

      onCell: () => cellWidth(150),

      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },

    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(140),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },

    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "project_name",

      key: "project_name",

      onCell: () => cellWidth(220),

      width: "220px",

      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "program_name",

      key: "program_name",

      onCell: () => cellWidth(220),

      width: "220px",

      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "department_name",

      key: "department_name",

      onCell: () => cellWidth(220),

      width: "220px",

      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Email Id",
        "email_id",
        setInventoryColumnSearch,
      ),

      dataIndex: "email_id",

      onCell: () => cellWidth(220),

      sorter: (a, b) => safeLocaleCompare(a, b, "email_id"),
    },

    {
      title: getSearchableHeader(
        "Check Name",
        "check_name",
        setInventoryColumnSearch,
      ),

      dataIndex: "check_name",

      onCell: () => cellWidth(220),

      sorter: (a, b) => safeLocaleCompare(a, b, "check_name"),
    },

    {
      title: getSearchableHeader(
        "Stopped At",
        "stopped_at",
        setInventoryColumnSearch,
      ),

      dataIndex: "stopped_at",

      onCell: () => cellWidth(220),

      sorter: (a, b) => safeLocaleCompare(a, b, "stopped_at"),

      render: (value: any) => {
        if (!value) return "-";

        return dayjs(value).format("DD-MM-YYYY");
      },
    },

    {
      title: "View Log",

      key: "viewLog",

      onCell: () => cellWidth(110),

      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const storedFilters = getLocalStorage("admin-stopped-cases-filters");

    if (storedFilters) {
      setFilterData(storedFilters);
    }
  }, []);

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Stopped_Checks_Report",
      sheetName: "Stopped Checks",
    });
  };
  return (
    <div>
      <DynamicSingleSelect
        fields={[
          "organization",
          "project",
          "location",
          "program",
          "department",
        ]}
        filterData={filterData}
        onFilterDataChange={(name, value, _labels) => {
          handleFilterChange(name, value);
        }}
        parentError={filterErrors}
        showButton={true}
        buttonText="Search"
        onButtonClick={handleSearch}
        disableButton={loading}
        onClearAll={handleClearAll}
      />

      {/* Table */}
      <div className="card mt-3">
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              rowKey="case_id"
              dataSource={filteredData}
              columns={columns}
              fixedHeader
              tableHeight={650}
              Selection={false}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* Timeline modal */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
    </div>
  );
};

export default StoppedCases;
